import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { ptForm } from 'yup-locale-pt';
import { yupResolver } from '@hookform/resolvers/yup';
import { CircularProgress, Grid, Stack, Typography } from '@mui/material';
import { MUIDataTableColumnDef } from 'mui-datatables';
import ActionsDataTable from '../../components/ActionsDataTable';
import useData from '../../services/hooks/useData';
import DialogForm from '../../components/DialogForm';
import {
  IAffiliateProductPayments,
  IAffiliateProducts,
  IPaymentMethods,
} from '../../models';
import AutocompleteAsync from '../../components/AutocompleteAsync';
import Button from '../../components/Button';
import { useCan } from '../../hooks/useCan';
import DialogX from '../../components/DialogX';
import DataTable from '../../components/DataTable';
import Title from '../../components/Title';

interface Props {
  affiliateProduct: IAffiliateProducts;
  open: boolean;
  onClose: () => void;
}

const schema = yup
  .object()
  .shape({
    payment_method: yup.object().required().nullable(),
  })
  .required();

yup.setLocale(ptForm);
yup.setLocale({ mixed: { notType: 'Valor inválido.' } });

export default function Payment({ affiliateProduct, open, onClose }: Props) {
  const {
    data,
    isLoading,
    isFetch,
    isSubmitting,
    create,
    deleteObject,
    getData,
  } = useData<IAffiliateProductPayments>(
    'affiliate/product/payment_methods',
    `?affiliate_product_id=${affiliateProduct.id}`,
    true
  );

  const isCreate = useCan({ view: 'product.create' });
  const isDelete = useCan({ view: 'product.delete' });

  const [isSubmittingX, setIsSubmittingX] = useState(false);
  const [isDialogX, setIsDialogX] = useState(false);
  const [payment, setPayment] = useState<IAffiliateProductPayments>(
    {} as IAffiliateProductPayments
  );

  useEffect(() => {
    if (open) {
      (async function get() {
        await getData();
      })();
    }
  }, [open, getData]);

  const { control, handleSubmit, reset } = useForm<IAffiliateProductPayments>({
    resolver: yupResolver(schema),
  });

  const getDescriptionPayment = (paymentMethod: IPaymentMethods) => {
    if (
      paymentMethod.type === 'debit card' ||
      paymentMethod.type === 'credit card'
    ) {
      return `${paymentMethod.description} ${paymentMethod.type_description}`;
    }

    return paymentMethod.description;
  };

  const paymentTemplate = (value: IPaymentMethods) => {
    return <div>{getDescriptionPayment(value)}</div>;
  };

  const handleClose = () => {
    onClose();
    reset({});
  };

  const mapColumns = (
    obj: IAffiliateProductPayments
  ): IAffiliateProductPayments => {
    const result: IAffiliateProductPayments = {
      affiliate_product_id: affiliateProduct.id || null,
      payment_method_id: obj.payment_method?.id || null,
    };

    return result;
  };

  const onSubmit = async (formData: IAffiliateProductPayments) => {
    const obj = mapColumns(formData);

    await create(obj);

    reset({});
  };

  const deletePayments = async (obj: IAffiliateProductPayments) => {
    if (obj.id && affiliateProduct.id) {
      setIsSubmittingX(true);
      await deleteObject(obj.id, `?affiliate_product_id=${affiliateProduct.id}`)
        .then(async () => {
          setIsDialogX(false);
        })
        .finally(() => setIsSubmittingX(false));
    }
  };

  const handleClosePayment = () => setIsDialogX(false);

  const setDialogPayment = (obj: IAffiliateProductPayments) => {
    setPayment(obj);
    setIsDialogX(true);
  };

  const onOk = async () => {
    await deletePayments(payment);
  };

  const actions = ActionsDataTable(
    false,
    data,
    isDelete ? setDialogPayment : undefined,
    undefined,
    true,
    undefined,
    !isDelete
  );

  const columns: MUIDataTableColumnDef[] = [
    {
      name: 'payment_method',
      label: 'Forma de Pagamento',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: IPaymentMethods) => paymentTemplate(value),
      },
    },
  ];

  if (actions) {
    columns.push(actions);
  }

  return (
    <>
      <DialogForm
        sx={{ minHeight: 450 }}
        titleDiag={affiliateProduct.product_name || 'Produto'}
        open={open}
        maxForm="sm"
        isLoading={isFetch}
        isSubmitting={isSubmitting}
        handleClose={handleClose}
        hideSubmit
        cancelText="Fechar"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div style={{ marginBottom: 15 }}>
          {isCreate && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={9}>
                <AutocompleteAsync
                  name="payment_method"
                  control={control}
                  title="Forma de Pagamento"
                  required
                  url="payment_methods"
                  getLabel={(option: IPaymentMethods) =>
                    getDescriptionPayment(option)
                  }
                  disabled={isSubmitting}
                  autoFocus
                  onChange={item => {
                    if (item) {
                      handleSubmit(onSubmit);
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Button
                  id="submit"
                  sx={{ mt: 3.6 }}
                  caption="Adicionar"
                  type="submit"
                  fullWidth
                  loading={isSubmitting}
                />
              </Grid>
            </Grid>
          )}
          <DataTable
            title={
              <Title description="Formas de Pagamentos" isLoading={isLoading} />
            }
            data={data}
            columns={columns}
            isLoading={isLoading}
            refresh={getData}
            hideActionAddToolbar
          />
        </div>
      </DialogForm>
      <DialogX
        titleDiag={affiliateProduct.product_name || 'Produto'}
        open={isDialogX}
        onOk={() => onOk()}
        captionOk="Confirmar"
        captionCancel="Fechar"
        handleClose={handleClosePayment}
        isLoading={isSubmittingX}
        autoFocus
      >
        {isSubmitting ? (
          <Stack
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress color="primary" />
          </Stack>
        ) : (
          <Typography variant="body2">
            {`Confirma a exclusão da forma de pagamento "${
              payment?.payment_method
                ? getDescriptionPayment(payment.payment_method)
                : ''
            }"`}
          </Typography>
        )}
      </DialogX>
    </>
  );
}
